import { useEffect, useState } from 'react';
import { Panel } from 'react-resizable-panels';

import { askAssistant } from '../../lib/ask-ai.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { logEditor } from '../../state/console.js';
import { emptyCell, type PixelDoc } from '../../lib/pixel-doc.js';
import { stepZoom, usePixel, type PixelTool } from '../../state/pixel.js';
import { filesOfKind, useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { SaveIndicator } from '../scene/SceneTab.js';
import { IconButton, isTyping, MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { Handle, SplitGroup } from '../ui/layout.js';
import { PixelCanvas } from './PixelCanvas.js';
import { PixelGenPanel } from './PixelGenPanel.js';
import { PixelSide } from './PixelSide.js';
import { PixelTimeline } from './PixelTimeline.js';
import { docFromImage, exportPng, readImagePixels } from './pixel-io.js';
import { WEBSITE, LOCAL_AI } from '../../lib/edition.js';
import { requireLocalGen } from '../../state/local-gen.js';

const TOOLS: Array<{ id: PixelTool; label: string; icon: IconName; key: string }> = [
  { id: 'pencil', label: 'Pencil', icon: 'pencil', key: 'B' },
  { id: 'eraser', label: 'Eraser', icon: 'eraser', key: 'E' },
  { id: 'fill', label: 'Fill', icon: 'bucket', key: 'G' },
  { id: 'line', label: 'Line', icon: 'line', key: 'L' },
  { id: 'rect', label: 'Rectangle', icon: 'square', key: 'U' },
  { id: 'ellipse', label: 'Ellipse', icon: 'circle', key: 'O' },
  { id: 'picker', label: 'Color picker (or Alt+click)', icon: 'pipette', key: 'I' },
  { id: 'select', label: 'Select', icon: 'marquee', key: 'M' },
  { id: 'move', label: 'Move selection / layer', icon: 'move', key: 'V' },
];

function slug(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'sprite';
}

function parseSize(text: string | null): [number, number] | null {
  const m = text?.match(/^\s*(\d+)\s*(?:[x×, ]\s*(\d+))?\s*$/i);
  if (!m) return null;
  const w = Math.min(4096, Math.max(1, Number(m[1])));
  const h = Math.min(4096, Math.max(1, Number(m[2] ?? m[1])));
  return [w, h];
}

/** Creates a new image document. `kind` picks the folder and default size;
 * `animated` is a looping flipbook texture (water, lava, fire, screens…). */
export async function newPixelArt(kind: 'sprite' | 'texture' | 'animated' = 'sprite', suggested?: string): Promise<string | null> {
  const isTexture = kind !== 'sprite';
  const name = await promptText(
    kind === 'animated'
      ? 'Name the animated texture (e.g. water, lava_flow, fire, tv_static)'
      : isTexture
        ? 'Name the texture (e.g. wood_planks, stone_wall, sword_blade)'
        : 'Name the image (e.g. hero, slime, grass_tiles)',
    suggested ?? (kind === 'animated' ? 'water' : isTexture ? 'texture' : 'sprite'),
  );
  if (!name?.trim()) return null;
  const size = parseSize(
    await promptText(
      'Size in pixels, any size up to 4096x4096 (e.g. 16x16, 64x64, 512x512, 1024x768)',
      kind === 'animated' ? '64x64' : isTexture ? '256x256' : '16x16',
    ),
  );
  if (!size) return null;
  let frames = 1;
  let fps = 8;
  if (kind === 'animated') {
    frames = Math.min(64, Math.max(2, Number(await promptText('Number of frames (2-64)', '8')) || 8));
    fps = Math.min(60, Math.max(1, Number(await promptText('Frames per second', '8')) || 8));
  }
  const folder = isTexture ? 'textures' : 'sprites';
  const path = name.includes('/') ? `${name.replace(/\.pixel\.json$/i, '')}.pixel.json` : `${folder}/${slug(name)}.pixel.json`;
  const init =
    kind === 'animated'
      ? (base: PixelDoc): PixelDoc => ({
          ...base,
          cells: Array.from({ length: frames }, () => base.layers.map(() => emptyCell(base))),
          animations: { loop: { frames: Array.from({ length: frames }, (_, i) => i), fps, loop: true } },
        })
      : undefined;
  await usePixel.getState().create(path, size[0], size[1], init);
  if (usePixel.getState().path !== path) return null;
  if (kind === 'animated') {
    usePixel.getState().set({ tiled: true, previewAnimation: 'loop' });
    logEditor(
      'info',
      `${path}: paint each frame, then use it on a material with "texture": "${path}", "textureAnimation": { "clip": "loop" } (add "scroll": [0.05, 0] to make it flow).`,
    );
  }
  return path;
}

async function importPng(path: string) {
  const pid = useWorkspace.getState().projectId;
  if (!pid) return;
  try {
    const img = await readImagePixels(pid, path);
    const size = parseSize(
      await promptText(`Frame size for ${path} (${img.width}×${img.height}). Sprite sheets are cut into frames row by row.`, `${img.width}x${img.height}`),
    );
    if (!size) return;
    const target = path.replace(/\.[a-z0-9]+$/i, '.pixel.json');
    await usePixel.getState().create(target, size[0], size[1], (base) => docFromImage(base, img, size[0], size[1]));
    logEditor('info', `Imported ${path} as ${target}`);
  } catch (error) {
    logEditor('error', `Could not import ${path}: ${error instanceof Error ? error.message : error}`);
  }
}

function FileMenu() {
  const entries = useProjectFiles((s) => s.entries);
  const path = usePixel((s) => s.path);
  const docs = filesOfKind(entries, 'pixel');
  const pngs = filesOfKind(entries, 'texture').filter((p) => p.toLowerCase().endsWith('.png'));
  const items = (): MenuItem[] => [
    { id: 'new', label: 'New image / sprite…', icon: 'plus', run: () => void newPixelArt('sprite') },
    { id: 'new-tex', label: 'New model texture…', icon: 'material', run: () => void newPixelArt('texture') },
    { id: 'new-anim', label: 'New animated texture…', icon: 'water', run: () => void newPixelArt('animated') },
    { id: 'h1', label: 'Pixel art', heading: true },
    ...docs.map((p) => ({ id: p, label: p, icon: 'pixel' as const, run: () => void usePixel.getState().open(p) })),
    ...(docs.length === 0 ? [{ id: 'none', label: 'No *.pixel.json files yet', disabled: true }] : []),
    ...(pngs.length
      ? [{ id: 'h2', label: 'Import PNG as pixel art', heading: true }, ...pngs.slice(0, 30).map((p) => ({ id: `png:${p}`, label: p, icon: 'image' as const, run: () => void importPng(p) }))]
      : []),
  ];
  return (
    <MenuButton className="picker" title="Open, create or import pixel art" items={items}>
      <Icon name="pixel" size={13} />
      <span className="picker-label">{path ? path.slice(path.lastIndexOf('/') + 1) : 'Pixel art'}</span>
      <Icon name="chevronDown" size={11} className="muted" />
    </MenuButton>
  );
}

function Toolbar() {
  const tool = usePixel((s) => s.tool);
  const brush = usePixel((s) => s.brush);
  const mirrorX = usePixel((s) => s.mirrorX);
  const mirrorY = usePixel((s) => s.mirrorY);
  const filled = usePixel((s) => s.filled);
  const contiguous = usePixel((s) => s.contiguous);
  const grid = usePixel((s) => s.grid);
  const zoom = usePixel((s) => s.zoom);
  const canUndo = usePixel((s) => s.past.length > 0);
  const canRedo = usePixel((s) => s.future.length > 0);
  const saveState = usePixel((s) => s.saveState);
  const doc = usePixel((s) => s.doc);
  const path = usePixel((s) => s.path);
  const s = usePixel.getState();
  return (
    <div className="viewport-toolbar pixel-toolbar">
      <FileMenu />
      <span className="bar-sep" />
      <div className="tb-group segmented">
        {TOOLS.map((t) => (
          <IconButton key={t.id} icon={t.icon} title={t.label} shortcut={t.key} active={tool === t.id} onClick={() => s.set({ tool: t.id })} disabled={!doc} />
        ))}
      </div>
      {(tool === 'pencil' || tool === 'eraser') && (
        <label className="pixel-brush" title="Brush size ([ and ])">
          <span className="muted">Size</span>
          <input type="number" min={1} max={128} value={brush} onChange={(e) => s.set({ brush: Math.max(1, Math.min(128, Number(e.target.value) || 1)) })} />
        </label>
      )}
      {(tool === 'rect' || tool === 'ellipse') && (
        <IconButton icon={tool === 'rect' ? 'square' : 'circle'} title="Filled shapes (Shift while dragging keeps it square)" active={filled} onClick={() => s.set({ filled: !filled })} />
      )}
      {tool === 'fill' && (
        <button type="button" className={`tb-btn${contiguous ? '' : ' is-active'}`} title="Fill every pixel of that color, not only touching ones" onClick={() => s.set({ contiguous: !contiguous })}>
          {contiguous ? 'Contiguous' : 'Global'}
        </button>
      )}
      <div className="tb-group segmented">
        <IconButton icon="mirror" title="Mirror horizontally while drawing" active={mirrorX} onClick={() => s.set({ mirrorX: !mirrorX })} />
        <button type="button" className={`tb-btn${mirrorY ? ' is-active' : ''}`} title="Mirror vertically while drawing" onClick={() => s.set({ mirrorY: !mirrorY })}>
          ⇕
        </button>
      </div>
      <MenuButton
        className="tb-btn"
        title="Transform"
        items={[
          { id: 'fh', label: 'Flip cel horizontally', run: () => s.flip(true, false) },
          { id: 'fv', label: 'Flip cel vertically', run: () => s.flip(false, false) },
          { id: 'fha', label: 'Flip all frames horizontally', run: () => s.flip(true, true) },
          { id: 'sep', label: '', separator: true },
          { id: 'l', label: 'Shift left 1px (wraps)', run: () => s.shift(-1, 0) },
          { id: 'r', label: 'Shift right 1px', run: () => s.shift(1, 0) },
          { id: 'u', label: 'Shift up 1px', run: () => s.shift(0, -1) },
          { id: 'd', label: 'Shift down 1px', run: () => s.shift(0, 1) },
        ]}
      >
        <Icon name="rotate" size={14} />
      </MenuButton>
      <span className="bar-sep" />
      <IconButton icon="grid" title="Pixel grid" active={grid} onClick={() => s.set({ grid: !grid })} />
      <div className="tb-group segmented">
        <IconButton icon="minus" title="Zoom out" shortcut="-" onClick={() => s.set({ zoom: stepZoom(zoom, -1) })} />
        <span className="pixel-zoom mono">{zoom}×</span>
        <IconButton icon="plus" title="Zoom in" shortcut="+" onClick={() => s.set({ zoom: stepZoom(zoom, 1) })} />
      </div>
      <span className="tb-spacer" />
      <button
        type="button"
        className="tb-btn"
        disabled={!doc || !path}
        title="Also write a PNG sprite sheet and .sprite.json next to this file (Prism Engine Cloud reads .pixel.json directly; export is for other tools)"
        onClick={async () => {
          const pid = useWorkspace.getState().projectId;
          if (!pid || !doc || !path) return;
          try {
            const png = await exportPng(pid, path, doc);
            logEditor('info', `Exported ${png}`);
          } catch (error) {
            logEditor('error', `Export failed: ${error instanceof Error ? error.message : error}`);
          }
        }}
      >
        Export PNG
      </button>
      <div className="tb-group segmented">
        <IconButton icon="undo" title="Undo" shortcut="Ctrl+Z" disabled={!canUndo} onClick={s.undo} />
        <IconButton icon="redo" title="Redo" shortcut="Ctrl+Y" disabled={!canRedo} onClick={s.redo} />
      </div>
      <SaveIndicator state={saveState} />
    </div>
  );
}

/** Text the Assistant gets from the pixel prompt bar. */
export function pixelRequest(prompt: string, path: string | null, info: { width: number; height: number; frames: number } | null): string {
  const ask = prompt.trim();
  const target = path ?? 'a new file sprites/<name>.pixel.json (pick a short snake_case name)';
  return [
    path ? `Edit the pixel art in ${path}: ${ask}` : `Create pixel art: ${ask}`,
    path && info ? `It is ${info.width}×${info.height} pixels with ${info.frames} frame(s).` : `Write it to ${target}.`,
    'Use the prism.pixel/1 format (see "Pixel art documents" in the engine docs): a palette of hex colors and, for each frame and layer, rows of palette characters (0-9A-Za-z, "." = transparent).',
    'Use a small cohesive palette, clean 1px outlines, readable silhouettes and consistent light from the top-left. Name animations (idle, run, ...) when there are several frames.',
    'Then check it with prism_check and look at it with prism_screenshot on a scene that uses it.',
  ].join('\n');
}

function AiBar() {
  const path = usePixel((s) => s.path);
  const doc = usePixel((s) => s.doc);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="ai-prompt pixel-ai">
      <form
        className="ai-prompt-row"
        onSubmit={async (e) => {
          e.preventDefault();
          if (busy || !text.trim()) return;
          setBusy(true);
          const ok = await askAssistant(pixelRequest(text, path, doc ? { width: doc.width, height: doc.height, frames: doc.cells.length } : null));
          setBusy(false);
          if (ok) setText('');
        }}
      >
        <Icon name="sparkles" size={16} className="ai-glyph" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={path ? 'Ask the AI to draw or change this art…' : 'Describe pixel art for the AI to draw…'}
          aria-label="Describe pixel art"
        />
        <button type="submit" className="primary small" disabled={busy || !text.trim()}>
          <Icon name="send" size={13} />
        </button>
      </form>
    </div>
  );
}

/** Aseprite-style pixel art editor for *.pixel.json. */
export function PixelTab({ active }: { active: boolean }) {
  const doc = usePixel((s) => s.doc);
  const error = usePixel((s) => s.error);
  const entries = useProjectFiles((s) => s.entries);
  const docs = filesOfKind(entries, 'pixel');
  const [side, setSide] = useState<'edit' | 'ai'>('edit');

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const s = usePixel.getState();
      if (!s.doc) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (ctrl && key === 'z' && !e.shiftKey) s.undo();
      else if (ctrl && (key === 'y' || (key === 'z' && e.shiftKey))) s.redo();
      else if (ctrl && key === 's') void s.flush();
      else if (ctrl && key === 'c') s.copySelection();
      else if (ctrl && key === 'x') s.cutSelection();
      else if (ctrl && key === 'v') s.paste();
      else if (ctrl && key === 'a') s.set({ selection: { x: 0, y: 0, w: s.doc.width, h: s.doc.height } });
      else if (ctrl || e.altKey) return;
      else if (e.key === 'Delete' || e.key === 'Backspace') s.deleteSelection();
      else if (e.key === 'Escape') s.set({ selection: null, playing: false });
      else if (e.key === 'Enter') s.set({ playing: !s.playing });
      else if (e.key === ',' || e.key === 'ArrowLeft') s.setFrame(s.frame - 1);
      else if (e.key === '.' || e.key === 'ArrowRight') s.setFrame(s.frame + 1);
      else if (e.key === '[') s.set({ brush: Math.max(1, s.brush - 1) });
      else if (e.key === ']') s.set({ brush: Math.min(128, s.brush + 1) });
      else if (e.key === '+' || e.key === '=') s.set({ zoom: stepZoom(s.zoom, 1) });
      else if (e.key === '-') s.set({ zoom: stepZoom(s.zoom, -1) });
      else if (key === 'x') s.swapColors();
      else {
        const t = TOOLS.find((t) => t.key.toLowerCase() === key);
        if (!t) return;
        s.set({ tool: t.id });
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="tab-body tab-pixel">
      <SplitGroup id="pixel-h" orientation="horizontal" fallback={{ center: 76, side: 24 }}>
        <Panel id="center" minSize={360} className="pane pane-center">
          <Toolbar />
          <SplitGroup id="pixel-v" orientation="vertical" fallback={{ canvas: 72, timeline: 28 }}>
            <Panel id="canvas" minSize={160} className="pixel-stage">
              <PixelCanvas />
              {!doc && (
                <div className="viewport-message">
                  <Icon name="pixel" size={22} />
                  <strong>{error ? 'Could not open the pixel art' : 'Open or create pixel art'}</strong>
                  {error && <span className="mono muted">{error}</span>}
                  <div className="scene-list">
                    {docs.slice(0, 8).map((p) => (
                      <button key={p} type="button" className="ghost small" onClick={() => void usePixel.getState().open(p)}>
                        {p}
                      </button>
                    ))}
                    <button type="button" className="primary small" onClick={() => void newPixelArt('sprite')}>
                      <Icon name="plus" size={13} /> New image / sprite
                    </button>
                    <button type="button" className="ghost small" onClick={() => void newPixelArt('texture')}>
                      <Icon name="material" size={13} /> New model texture
                    </button>
                    <button type="button" className="ghost small" onClick={() => void newPixelArt('animated')}>
                      <Icon name="water" size={13} /> New animated texture
                    </button>
                  </div>
                  <span className="muted">Any size up to 4096×4096. Sprites, tilemaps and model materials use .pixel.json files directly.</span>
                </div>
              )}
              <AiBar />
            </Panel>
            <Handle orientation="horizontal" />
            <Panel id="timeline" minSize={110} className="pane">
              <PixelTimeline />
            </Panel>
          </SplitGroup>
        </Panel>
        <Handle orientation="vertical" />
        <Panel id="side" minSize={230} className="pane">
          <div className="subtabs panel-subtabs" role="tablist">
            <button type="button" role="tab" aria-selected={side === 'edit'} className={`subtab${side === 'edit' ? ' is-active' : ''}`} onClick={() => setSide('edit')}>
              Pixel Art
            </button>
            {LOCAL_AI && (
              <button type="button" role="tab" aria-selected={side === 'ai'} className={`subtab${side === 'ai' ? ' is-active' : ''}`} onClick={() => requireLocalGen() && setSide('ai')} title="Generate pixel art on this PC with a configurable pipeline">
                AI Generate
              </button>
            )}
          </div>
          {side === 'edit' || !LOCAL_AI ? <PixelSide /> : <PixelGenPanel />}
        </Panel>
      </SplitGroup>
    </div>
  );
}
